import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCommercialHandoffData,
  createSalesOrder,
  updateSalesOrder,
  markOrderAsPrinted,
  addPayment,
  listPayments,
  getFELConfigStatus,
  getFEL,
  issueFEL,
  openFELPdf,
  getTryOnQuota,
  CommercialHandoffData,
  SalesPayment,
  PaymentMethod,
  FELDocument,
  TryOnQuotaResponse,
} from '../services/api';
import { useWorkOrderPDF } from '../hooks/useWorkOrderPDF';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';

interface ExtraItem {
  description: string;
  price: string;
}

const formatValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return value.toString();
};

const formatAxis = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return `${value}°`;
};

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('es-GT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const parseCurrency = (value: string): number => {
  const cleaned = value.replace(/[,]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

export const CommercialHandoff = () => {
  const { visit_id } = useParams<{ visit_id: string }>();
  const navigate = useNavigate();
  const { generatePDF, isGenerating: isGeneratingPDF } = useWorkOrderPDF();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CommercialHandoffData | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);

  const [lensDescription, setLensDescription] = useState('');
  const [lensPrice, setLensPrice] = useState('');
  const [frameDescription, setFrameDescription] = useState('');
  const [framePrice, setFramePrice] = useState('');
  const [extras, setExtras] = useState<ExtraItem[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [paymentNote, setPaymentNote] = useState('');
  const [payments, setPayments] = useState<SalesPayment[]>([]);

  // FEL (Guatemala electronic tax invoice) — opt-in per clinic
  const [felEnabled, setFelEnabled] = useState(false);
  const [felDoc, setFelDoc] = useState<FELDocument | null>(null);
  const [felBusy, setFelBusy] = useState(false);
  const [felError, setFelError] = useState<string | null>(null);

  // Virtual try-on monthly quota
  const [tryOnQuota, setTryOnQuota] = useState<TryOnQuotaResponse | null>(null);

  const paymentMethodLabels: Record<PaymentMethod, string> = {
    cash: 'Efectivo',
    card: 'Tarjeta',
    transfer: 'Transferencia',
  };

  const loadData = async () => {
    if (!visit_id) return;

    try {
      setLoading(true);
      const response = await getCommercialHandoffData(visit_id);

      if (response.success && response.data) {
        setData(response.data);

        // Load existing sales order data if available
        if (response.data.sales_order) {
          setOrderId(response.data.sales_order.id);
          try {
            const paymentsResponse = await listPayments(response.data.sales_order.id);
            setPayments(paymentsResponse.data || []);
          } catch {
            setPayments([]);
          }
          // If FEL is enabled, load any already-issued invoice for this order
          try {
            const status = await getFELConfigStatus();
            setFelEnabled(status.enabled);
            if (status.enabled) {
              setFelDoc(await getFEL(response.data.sales_order.id));
            }
          } catch {
            setFelEnabled(false);
          }
        }

        if (response.data.optical_details) {
          setLensDescription(response.data.optical_details.lens_description || '');
          setLensPrice(response.data.optical_details.lens_price ? formatCurrency(response.data.optical_details.lens_price) : '');
          setFrameDescription(response.data.optical_details.frame_description || '');
          setFramePrice(response.data.optical_details.frame_price ? formatCurrency(response.data.optical_details.frame_price) : '');
        }

        if (response.data.extras && response.data.extras.length > 0) {
          setExtras(response.data.extras.map(extra => ({
            description: extra.description,
            price: formatCurrency(extra.price),
          })));
        } else {
          setExtras([]);
        }
      } else {
        setError(response.error || 'No se pudo cargar la información');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocurrió un error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [visit_id]);

  useEffect(() => {
    getTryOnQuota()
      .then(setTryOnQuota)
      .catch(() => setTryOnQuota(null));
  }, []);

  const isModified = () => {
    if (!data) return false;
    const origLensDesc = data.optical_details?.lens_description || '';
    const origLensPrice = data.optical_details?.lens_price || 0;
    const origFrameDesc = data.optical_details?.frame_description || '';
    const origFramePrice = data.optical_details?.frame_price || 0;

    if (lensDescription !== origLensDesc) return true;
    if (parseCurrency(lensPrice) !== origLensPrice) return true;
    if (frameDescription !== origFrameDesc) return true;
    if (parseCurrency(framePrice) !== origFramePrice) return true;

    const origExtras = data.extras || [];
    if (extras.length !== origExtras.length) return true;

    for (let i = 0; i < extras.length; i++) {
      if (extras[i].description !== origExtras[i].description) return true;
      if (parseCurrency(extras[i].price) !== origExtras[i].price) return true;
    }

    return false;
  };

  const handleUpdateOrder = async () => {
    const { grandTotal } = calculateTotals();
    const paidAmount = data?.sales_order?.amount_paid || 0;

    if (grandTotal <= 0) {
      setError('El gran total no puede ser 0');
      return;
    }
    if (grandTotal < paidAmount) {
      setError(`El gran total no puede ser menor al monto ya pagado (Q ${formatCurrency(paidAmount)})`);
      return;
    }

    const savedOrderId = await handleSaveOrder();
    if (savedOrderId) {
      await loadData();
    }
  };

  const calculateTotals = () => {
    const lens = parseCurrency(lensPrice);
    const frame = parseCurrency(framePrice);
    const subtotalOptical = lens + frame;

    const subtotalExtras = extras.reduce((sum, extra) => {
      return sum + parseCurrency(extra.price);
    }, 0);

    const grandTotal = subtotalOptical + subtotalExtras;

    return { subtotalOptical, subtotalExtras, grandTotal };
  };

  const handleAddExtra = () => {
    setExtras([...extras, { description: '', price: '' }]);
  };

  const handleRemoveExtra = (index: number) => {
    setExtras(extras.filter((_, i) => i !== index));
  };

  const handleExtraChange = (index: number, field: 'description' | 'price', value: string) => {
    const newExtras = [...extras];
    newExtras[index][field] = value;
    setExtras(newExtras);
  };

  const handleSaveOrder = async (): Promise<number | null> => {
    if (!visit_id || !data) return null;

    try {
      setIsSaving(true);

      const { subtotalOptical, subtotalExtras, grandTotal } = calculateTotals();

      const extrasData = extras.map(extra => ({
        description: extra.description,
        price: parseCurrency(extra.price),
      }));

      if (orderId) {
        // Update existing order
        await updateSalesOrder(orderId, {
          lens_description: lensDescription,
          lens_price: parseCurrency(lensPrice) || null,
          frame_description: frameDescription,
          frame_price: parseCurrency(framePrice) || null,
          extras: extrasData,
          subtotal_optical: subtotalOptical,
          subtotal_extras: subtotalExtras,
          grand_total: grandTotal,
        });
        return orderId;
      } else {
        // Create new order
        const response = await createSalesOrder({
          visit_id: parseInt(visit_id),
          lens_description: lensDescription,
          lens_price: parseCurrency(lensPrice) || null,
          frame_description: frameDescription,
          frame_price: parseCurrency(framePrice) || null,
          extras: extrasData,
        });

        if (response.success && response.data) {
          setOrderId(response.data.order_id);
          return response.data.order_id;
        }
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la orden de venta');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!visit_id) return;

    let currentOrderId = orderId;
    if (!currentOrderId) {
      currentOrderId = await handleSaveOrder();
      if (!currentOrderId) return;
    }

    try {
      const pdfOrderId = await generatePDF(visit_id);

      if (pdfOrderId) {
        await markOrderAsPrinted(pdfOrderId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo generar el PDF');
    }
  };

  const handleTryOn = async () => {
    if (!visit_id || !frameDescription || tryOnQuotaExceeded) return;
    await handleSaveOrder();
    navigate(`/commercial-handoff/${visit_id}/try-on`, { state: { frameDescription } });
  };

  // Monthly try-on quota pill shown next to "Probar lentes": blue while
  // there's room, amber near the limit, red once it's hit, gray when the
  // clinic has no limit configured at all (generation disabled).
  const tryOnQuotaExceeded =
    tryOnQuota !== null && (!tryOnQuota.configured || tryOnQuota.used >= tryOnQuota.limit);

  const tryOnQuotaPill = (() => {
    if (!tryOnQuota) return null;
    if (!tryOnQuota.configured) {
      return { bg: 'bg-gray-100', text: 'text-gray-500', icon: 'lock', label: 'Generación no habilitada' };
    }
    if (tryOnQuota.used >= tryOnQuota.limit) {
      return {
        bg: 'bg-red-50',
        text: 'text-red-600',
        icon: 'block',
        label: `Límite alcanzado (${tryOnQuota.used}/${tryOnQuota.limit})`,
      };
    }
    if (tryOnQuota.limit - tryOnQuota.used <= 2) {
      return {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        icon: 'hourglass_top',
        label: `${tryOnQuota.used}/${tryOnQuota.limit} este mes`,
      };
    }
    return {
      bg: 'bg-blue-50',
      text: 'text-primary',
      icon: 'auto_awesome',
      label: `${tryOnQuota.used}/${tryOnQuota.limit} este mes`,
    };
  })();

  const goToPatientHistory = () => {
    if (data?.patient?.id) {
      navigate(`/history/${data.patient.id}`);
    } else {
      navigate('/home');
    }
  };

  const handleIssueFEL = async () => {
    // FEL must be attached to a saved order.
    let currentOrderId = orderId;
    if (!currentOrderId) {
      currentOrderId = await handleSaveOrder();
      if (!currentOrderId) return;
    }
    const receptorNit = window.prompt(
      'NIT del receptor (deje "CF" para Consumidor Final):',
      felDoc?.receptor_nit || 'CF'
    );
    if (receptorNit === null) return; // cancelled
    try {
      setFelBusy(true);
      setFelError(null);
      const doc = await issueFEL(currentOrderId, receptorNit.trim());
      setFelDoc(doc);
    } catch (err) {
      setFelError(err instanceof Error ? err.message : 'No se pudo emitir la factura');
    } finally {
      setFelBusy(false);
    }
  };

  const handleViewFELPdf = async () => {
    if (!orderId) return;
    try {
      setFelBusy(true);
      setFelError(null);
      await openFELPdf(orderId);
    } catch (err) {
      setFelError(err instanceof Error ? err.message : 'No se pudo abrir el PDF');
    } finally {
      setFelBusy(false);
    }
  };

  const handleAddPaymentClick = async () => {
    // A payment must be attached to a saved order.
    let currentOrderId = orderId;
    if (!currentOrderId) {
      currentOrderId = await handleSaveOrder();
      if (!currentOrderId) return;
    }
    setPaymentAmount('');
    setPaymentMethod('cash');
    setPaymentNote('');
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!orderId) return;
    const amount = parseCurrency(paymentAmount);
    const completesOrder = totalPaid + amount >= grandTotal - 0.005;
    try {
      setIsSaving(true);
      await addPayment(orderId, { amount, method: paymentMethod, note: paymentNote || undefined });
      setShowPaymentModal(false);
      if (completesOrder) {
        // Order is fully paid — the backend auto-completes it; return to history.
        goToPatientHistory();
        return;
      }
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo registrar el pago');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (data?.patient?.id) {
      navigate(`/history/${data.patient.id}`);
    } else {
      navigate('/home');
    }
  };

  const { subtotalOptical, subtotalExtras, grandTotal } = calculateTotals();
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const pendingAmount = Math.max(grandTotal - totalPaid, 0);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f6f7f8]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-[#101822] font-medium">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f6f7f8]">
        <div className="text-center">
          <p className="text-red-600 font-medium">{error || 'No hay información disponible'}</p>
          <button
            onClick={handleBack}
            className="mt-4 rounded-xl bg-primary px-6 py-2 text-white font-bold"
          >
            Regresar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f6f7f8]">
      {/* Header */}
      <header className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 md:px-8">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            onClick={handleBack}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#101822] hover:bg-gray-100"
          >
            <span className="material-symbols-outlined text-3xl">arrow_back</span>
          </button>
          <h1 className="text-sm sm:text-xl font-bold text-[#101822] truncate">{data.patient?.full_name || 'Paciente'}</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex flex-1 flex-col gap-4 p-4 md:gap-8 md:p-8 lg:flex-row">
        {/* Left Panel - Medical Reference (Read-Only) */}
        <div className="flex w-full flex-col gap-4 lg:w-[30%]">
          <div className="flex flex-col rounded-xl border border-gray-200 bg-slate-100 p-6">
            <h2 className="text-[#101822] text-lg font-bold tracking-[-0.015em] mb-4">
              Receta Médica (Referencia)
            </h2>

            {/* Rx Grid */}
            <div className="overflow-x-auto">
              <div className="grid grid-cols-[auto_1fr_1fr_1fr_1fr] gap-x-2 gap-y-2 text-sm text-center min-w-[360px]">
                <div className="font-medium text-gray-500"></div>
                <div className="font-medium text-gray-500">Esfera</div>
                <div className="font-medium text-gray-500">Cilindro</div>
                <div className="font-medium text-gray-500">Eje</div>
                <div className="font-medium text-gray-500">Adición</div>

                <div className="font-bold text-gray-800 py-2 text-left">Lejos OD</div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.od_sphere_far)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.od_cylinder_far)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatAxis(data.refraction?.od_axis_far)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.od_add_far)}
                </div>

                <div className="font-bold text-gray-800 py-2 text-left">Lejos OI</div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.oi_sphere_far)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.oi_cylinder_far)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatAxis(data.refraction?.oi_axis_far)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.oi_add_far)}
                </div>

                <div className="font-bold text-gray-800 py-2 text-left">Cerca OD</div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.od_sphere_near)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.od_cylinder_near)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatAxis(data.refraction?.od_axis_near)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.od_add_near)}
                </div>

                <div className="font-bold text-gray-800 py-2 text-left">Cerca OI</div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.oi_sphere_near)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.oi_cylinder_near)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatAxis(data.refraction?.oi_axis_near)}
                </div>
                <div className="text-gray-800 rounded-lg py-2">
                  {formatValue(data.refraction?.oi_add_near)}
                </div>
              </div>
            </div>

            {/* DIP/DNP */}
            <div className="mt-6 flex justify-between text-sm">
              <div className="flex flex-col gap-1">
                <span className="font-medium text-gray-500">DIP:</span>
                <span className="text-base font-semibold text-[#101822]">
                  {data.visual_acuity?.od_dip_mm || data.visual_acuity?.oi_dip_mm
                    ? `${data.visual_acuity.od_dip_mm || data.visual_acuity.oi_dip_mm} mm`
                    : 'N/A'}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-medium text-gray-500">DNP:</span>
                <span className="text-base font-semibold text-[#101822]">
                  {data.visual_acuity?.od_dnp_mm && data.visual_acuity?.oi_dnp_mm
                    ? `${data.visual_acuity.od_dnp_mm}/${data.visual_acuity.oi_dnp_mm} mm`
                    : 'N/A'}
                </span>
              </div>
            </div>

            {/* Diagnosis */}
            <div className="mt-6">
              <p className="text-sm font-medium text-gray-500 mb-2">Diagnóstico:</p>
              <p className="text-sm text-gray-800">{data.visit?.diagnosis || 'Sin diagnóstico'}</p>
            </div>
          </div>
        </div>

        {/* Right Panel - Sales Input Form */}
        <div className="flex w-full flex-col gap-4 lg:w-[70%]">
          <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-6">
            <div className="flex-grow space-y-8">
              {/* Lenses Section */}
              <div>
                <h2 className="text-[#101822] text-[22px] font-bold leading-tight tracking-[-0.015em] pb-4">
                  Lentes
                </h2>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <label className="flex flex-col">
                    <p className="text-[#101822] text-base font-medium leading-normal pb-2">
                      Descripción
                    </p>
                    <input
                      className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl text-[#101822] focus:outline-0 focus:ring-2  border border-gray-300 bg-white focus:border-primary h-14 placeholder:text-gray-400 p-[15px] text-base font-normal leading-normal"
                      value={lensDescription}
                      onChange={(e) => setLensDescription(e.target.value)}
                      placeholder="Ej. Polycarbonate Blue Block"
                    />
                  </label>
                  <label className="flex flex-col">
                    <p className="text-[#101822] text-base font-medium leading-normal pb-2">Precio</p>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-lg font-semibold [#101822]">
                        Q
                      </span>
                      <input
                        className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl [#101822] font-semibold focus:outline-0 focus:ring-2 border border-gray-300 bg-white focus:border-primary h-14 placeholder:text-gray-400 pl-9 p-[15px] text-lg leading-normal"
                        value={lensPrice}
                        onChange={(e) => setLensPrice(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </label>
                </div>
              </div>

              {/* Frame Section */}
              <div>
                <h2 className="text-[#101822] text-[22px] font-bold leading-tight tracking-[-0.015em] pb-4">
                  Armazón
                </h2>
                <div className="flex flex-wrap items-center gap-3 pb-6">
                  <SecondaryButton
                    onClick={handleTryOn}
                    disabled={!frameDescription || isSaving || tryOnQuotaExceeded}
                    icon={<span className="material-symbols-outlined text-lg">photo_camera</span>}
                    className="px-4 py-2 text-sm"
                  >
                    Probar lentes
                  </SecondaryButton>
                  {tryOnQuotaPill && (
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${tryOnQuotaPill.bg} ${tryOnQuotaPill.text}`}
                    >
                      <span className="material-symbols-outlined text-sm">{tryOnQuotaPill.icon}</span>
                      {tryOnQuotaPill.label}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <label className="flex flex-col">
                    <p className="text-[#101822] text-base font-medium leading-normal pb-2">
                      Descripción
                    </p>
                    <input
                      className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl text-[#101822] focus:outline-0 focus:ring-2  border border-gray-300 bg-white focus:border-primary h-14 placeholder:text-gray-400 p-[15px] text-base font-normal leading-normal"
                      value={frameDescription}
                      onChange={(e) => setFrameDescription(e.target.value)}
                      placeholder="Ej. Ray-Ban 4520 Black"
                    />
                  </label>
                  <label className="flex flex-col">
                    <p className="text-[#101822] text-base font-medium leading-normal pb-2">Precio</p>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-lg font-semibold [#101822]">
                        Q
                      </span>
                      <input
                        className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-xl [#101822] font-semibold focus:outline-0 focus:ring-2 border border-gray-300 bg-white focus:border-primary h-14 placeholder:text-gray-400 pl-9 p-[15px] text-lg leading-normal"
                        value={framePrice}
                        onChange={(e) => setFramePrice(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </label>
                </div>
              </div>

              {/* Subtotal */}
              <div className="flex justify-end pt-2">
                <p className="text-sm font-medium text-gray-500">
                  Subtotal Lentes + Armazón:{' '}
                  <span className="text-[#101822] font-bold">Q {formatCurrency(subtotalOptical)}</span>
                </p>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200"></div>

              {/* Extras Section */}
              <div>
                <div className="flex items-center justify-between pb-4">
                  <h2 className="text-[#101822] text-[22px] font-bold leading-tight tracking-[-0.015em]">
                    Extras / Otros
                  </h2>
                  <button
                    onClick={handleAddExtra}
                    className="group flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-bold [#101822] hover:bg-emerald-50 transition-colors"
                  >
                    <span>➕</span>
                    <span>Agregar Producto</span>
                  </button>
                </div>

                {extras.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {extras.map((extra, index) => (
                      <div key={index} className="flex items-end gap-4">
                        <label className="flex flex-col flex-1 min-w-0">
                          <span className="text-[#101822] text-sm font-medium leading-normal pb-2">
                            Descripción
                          </span>
                          <input
                            className="form-input flex w-full min-w-0 resize-none overflow-hidden rounded-xl text-[#101822] focus:outline-0 focus:ring-2  border border-gray-300 bg-white focus:border-primary h-12 placeholder:text-gray-400 px-4 text-base font-normal leading-normal"
                            placeholder="Ej. Gotas lubricantes"
                            value={extra.description}
                            onChange={(e) => handleExtraChange(index, 'description', e.target.value)}
                          />
                        </label>
                        <label className="flex flex-col w-32 shrink-0">
                          <span className="text-[#101822] text-sm font-medium leading-normal pb-2">
                            Precio
                          </span>
                          <div className="relative">
                            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-base font-semibold [#101822]">
                              Q
                            </span>
                            <input
                              className="form-input flex w-full min-w-0 resize-none overflow-hidden rounded-xl [#101822] font-semibold focus:outline-0 focus:ring-2 border border-gray-300 bg-white focus:border-primary h-12 placeholder:text-gray-400 pl-8 pr-3 text-base leading-normal text-right"
                              value={extra.price}
                              onChange={(e) => handleExtraChange(index, 'price', e.target.value)}
                              placeholder="0.00"
                            />
                          </div>
                        </label>
                        <button
                          onClick={() => handleRemoveExtra(index)}
                          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:border-red-300 transition-colors"
                          title="Eliminar"
                        >
                          <span className="material-symbols-outlined text-xl">delete</span>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm italic">No hay extras agregados</p>
                )}
              </div>

              {/* Financial Summary */}
              <div className="mt-8 rounded-xl bg-gray-50 p-6 border border-gray-100">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-base text-gray-500 font-medium">
                      Subtotal Lentes + Armazón
                    </span>
                    <span className="text-base text-gray-500 font-medium">
                      Q {formatCurrency(subtotalOptical)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-base text-gray-500 font-medium">Total Extras</span>
                    <span className="text-base text-gray-500 font-medium">
                      Q {formatCurrency(subtotalExtras)}
                    </span>
                  </div>
                  <div className="h-px bg-gray-200 my-2"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-[#101822]">GRAN TOTAL</span>
                    <span className="text-2xl font-bold [#101822]">
                      Q {formatCurrency(grandTotal)}
                    </span>
                  </div>

                  {payments.length > 0 && (
                    <>
                      <div className="h-px bg-gray-200 my-2"></div>
                      <div className="space-y-2">
                        <span className="text-sm font-semibold text-gray-700">Pagos registrados</span>
                        {payments.map((p) => (
                          <div key={p.id} className="flex justify-between items-center text-sm">
                            <span className="text-gray-500">
                              {new Date(p.paid_at).toLocaleDateString('es-GT')} · {paymentMethodLabels[p.method]}
                              {p.note ? ` · ${p.note}` : ''}
                            </span>
                            <span className="text-gray-700 font-medium">Q {formatCurrency(p.amount)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-base text-gray-500 font-medium">Total Pagado</span>
                        <span className="text-base text-green-600 font-semibold">Q {formatCurrency(totalPaid)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-base text-gray-500 font-medium">Pendiente</span>
                        <span className="text-base font-semibold text-[#101822]">Q {formatCurrency(pendingAmount)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Add Payment (Abono) Modal */}
      {showPaymentModal && (() => {
        const amount = parseCurrency(paymentAmount);
        const isAmountTooHigh = amount > pendingAmount + 0.005;
        const isAmountInvalid = amount <= 0 || isAmountTooHigh;

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Registrar Pago</h3>
              <p className="text-gray-600 mb-1">Total: Q {formatCurrency(grandTotal)}</p>
              <p className="text-gray-600 mb-4">Pendiente: Q {formatCurrency(pendingAmount)}</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Monto (Q)</label>
                <input
                  type="text"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                  className={`w-full rounded-xl border px-4 py-3 text-lg font-medium focus:outline-none focus:ring-2 transition-colors ${isAmountTooHigh
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  autoFocus
                />
                {isAmountTooHigh && (
                  <p className="text-sm text-red-600 mt-2">
                    El monto no puede ser mayor al pendiente (Q {formatCurrency(pendingAmount)}).
                  </p>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Método</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="cash">Efectivo</option>
                  <option value="card">Tarjeta</option>
                  <option value="transfer">Transferencia</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Nota (opcional)</label>
                <input
                  type="text"
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder="No. de transferencia, referencia…"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex gap-3">
                <SecondaryButton
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-3"
                >
                  Cancelar
                </SecondaryButton>
                <PrimaryButton
                  onClick={handleConfirmPayment}
                  disabled={isSaving || isAmountInvalid}
                  className="flex-1 px-4 py-3"
                >
                  {isSaving ? 'Procesando...' : 'Registrar Pago'}
                </PrimaryButton>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Footer */}
      <footer className="sticky bottom-0 mt-auto bg-white/80 backdrop-blur-sm border-t border-gray-200 p-4 md:px-8">
        {felError && (
          <p className="mb-3 text-sm font-medium text-red-600">{felError}</p>
        )}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <button
            onClick={handleGeneratePDF}
            disabled={isGeneratingPDF || (!orderId && !lensDescription && !frameDescription)}
            className="flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-gray-300 bg-transparent px-6 py-4 text-base font-bold text-gray-700 shadow-sm transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingPDF ? 'Generando...' : 'Orden de trabajo'}
          </button>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:ml-auto">
            {/* FEL: only rendered when the clinic has the feature enabled */}
            {felEnabled && (
              felDoc ? (
                <SecondaryButton
                  onClick={handleViewFELPdf}
                  disabled={felBusy}
                  className="w-full sm:w-auto px-6 py-4"
                  title={`Factura autorizada: ${felDoc.authorization_number}`}
                >
                  {felBusy ? 'Abriendo...' : 'Ver Factura (PDF)'}
                </SecondaryButton>
              ) : (
                <SecondaryButton
                  onClick={handleIssueFEL}
                  disabled={felBusy || (!orderId && !lensDescription && !frameDescription)}
                  className="w-full sm:w-auto px-6 py-4"
                >
                  {felBusy ? 'Emitiendo...' : 'Emitir Factura'}
                </SecondaryButton>
              )
            )}
            {orderId && (
              <SecondaryButton
                onClick={handleUpdateOrder}
                disabled={isSaving || !isModified()}
                className="w-full sm:w-auto px-6 py-4"
              >
                {isSaving ? 'Procesando...' : 'Actualizar Orden'}
              </SecondaryButton>
            )}
            <PrimaryButton
              onClick={handleAddPaymentClick}
              disabled={isSaving || (!orderId && !lensDescription && !frameDescription)}
              className="w-full sm:w-auto px-8 py-4 text-lg"
            >
              {isSaving ? 'Procesando...' : 'Registrar Pago'}
            </PrimaryButton>
          </div>
        </div>
      </footer>
    </div>
  );
};
